import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  TextInput,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, DrawerActions, useNavigationState } from '@react-navigation/native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { notificationsApi } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../hooks';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';

import { tokens } from '@design';
// ============================================================
// SVG Icon Components
// ============================================================

const SearchIcon = ({ size = 20, color = COLORS.textLight }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BellIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SunIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" pointerEvents="none">
    <Path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 2V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4.93 4.93L6.34 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17.66 17.66L19.07 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6.34 17.66L4.93 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.07 4.93L17.66 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FilterIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 21V14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 10V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 21V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 8V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 21V16" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M1 14H7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 8H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 16H23" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 24, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 12H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 6H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 18H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MoonIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" pointerEvents="none">
    <Path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Back Arrow Icon (pointing right for RTL - رجوع)
const BackArrowIcon = ({ size = 24, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SettingsIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" pointerEvents="none">
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.1441C4.69632 20.0435 4.47575 19.896 4.29 19.71C4.10405 19.5243 3.95653 19.3037 3.85588 19.0609C3.75523 18.8181 3.70343 18.5578 3.70343 18.295C3.70343 18.0322 3.75523 17.7719 3.85588 17.5291C3.95653 17.2863 4.10405 17.0657 4.29 16.88L4.35 16.82C4.58054 16.5843 4.73519 16.285 4.794 15.9606C4.85282 15.6362 4.81312 15.3016 4.68 15C4.55324 14.7042 4.34276 14.452 4.07447 14.2743C3.80618 14.0966 3.49179 14.0013 3.17 14H3C2.46957 14 1.96086 13.7893 1.58579 13.4142C1.21071 13.0391 1 12.5304 1 12C1 11.4696 1.21071 10.9609 1.58579 10.5858C1.96086 10.2107 2.46957 10 3 10H3.09C3.42099 9.99229 3.74197 9.88513 4.01118 9.69252C4.28038 9.4999 4.48551 9.23074 4.6 8.92C4.73312 8.61838 4.77282 8.28381 4.714 7.95941C4.65519 7.63502 4.50054 7.33568 4.27 7.1L4.21 7.04C4.02405 6.85425 3.87653 6.63368 3.77588 6.39088C3.67523 6.14808 3.62343 5.88783 3.62343 5.625C3.62343 5.36217 3.67523 5.10192 3.77588 4.85912C3.87653 4.61632 4.02405 4.39575 4.21 4.21C4.39575 4.02405 4.61632 3.87653 4.85912 3.77588C5.10192 3.67523 5.36217 3.62343 5.625 3.62343C5.88783 3.62343 6.14808 3.67523 6.39088 3.77588C6.63368 3.87653 6.85425 4.02405 7.04 4.21L7.1 4.27C7.33568 4.50054 7.63502 4.65519 7.95941 4.714C8.28381 4.77282 8.61838 4.73312 8.92 4.6H9C9.29577 4.47324 9.54802 4.26276 9.72569 3.99447C9.90337 3.72618 9.99872 3.41179 10 3.09V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Module-level debounce state (shared across all Navbar instances)
// ============================================================
let globalLastFetchTime = 0;
let globalIntervalId: NodeJS.Timeout | null = null;
let globalNotificationCount = 0;
const globalListeners: Set<(count: number) => void> = new Set();

// ============================================================
// Navbar Props Interface
// ============================================================

interface NavbarProps {
  title?: string;
  notificationCount?: number;
  onSearch?: (query: string) => void;
  onNotificationPress?: () => void;
  onFilterPress?: () => void;
  onThemeToggle?: () => void;
  showSearch?: boolean;
  showFilter?: boolean;
}

// ============================================================
// Navbar Component
// ============================================================

export default function Navbar({
  title,
  notificationCount: externalNotificationCount,
  onSearch,
  onNotificationPress,
  onFilterPress,
  onThemeToggle,
  showSearch = true,
  showFilter = true,
}: NavbarProps) {
  const navigation = useNavigation();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  // Use provided title or fall back to translated system name
  const displayTitle = title || t('auth.systemName');

  // Check if we can go back in navigation history
  const canGoBack = navigation.canGoBack();

  // Check if user is executive (متخذ القرار) - they don't see notifications
  const isExecutive = user?.profile?.user_type === 'executive';
  const [internalNotificationCount, setInternalNotificationCount] = useState(0);
  const { isDark, toggleTheme, colors } = useTheme();
  const { isMobile, isTablet, isDesktop, containerPadding } = useResponsive();

  // Use external count if provided, otherwise use internal count
  const notificationCount = externalNotificationCount ?? internalNotificationCount;

  const DEBOUNCE_MS = 5000; // Minimum 5 seconds between requests
  const POLLING_INTERVAL_MS = 60000; // 60 seconds between polls

  // Shared fetch function that updates all Navbar instances
  const fetchNotificationCount = useCallback(async () => {
    if (!isAuthenticated || isExecutive) {
      return;
    }

    // Global debounce - prevent fetching more than once per DEBOUNCE_MS across ALL Navbar instances
    const now = Date.now();
    if (now - globalLastFetchTime < DEBOUNCE_MS) {
      return;
    }
    globalLastFetchTime = now;

    try {
      const response = await notificationsApi.getUnreadCount();
      const count = response.unread_count || 0;
      globalNotificationCount = count;
      // Notify all listeners (other Navbar instances)
      globalListeners.forEach(listener => listener(count));
    } catch (error) {
      // Silently fail - don't show error for notification count
      console.log('Could not fetch notification count');
    }
  }, [isAuthenticated, isExecutive]);

  // Subscribe to global notification count updates
  useEffect(() => {
    if (!isAuthenticated || isExecutive) {
      return;
    }

    // Register this component as a listener
    const listener = (count: number) => {
      setInternalNotificationCount(count);
    };
    globalListeners.add(listener);

    // Initialize with current global count
    if (globalNotificationCount > 0) {
      setInternalNotificationCount(globalNotificationCount);
    }

    // Only fetch if no recent fetch (global debounce)
    const now = Date.now();
    if (now - globalLastFetchTime >= DEBOUNCE_MS) {
      fetchNotificationCount();
    }

    // Start global interval only if not already running
    if (!globalIntervalId) {
      globalIntervalId = setInterval(fetchNotificationCount, POLLING_INTERVAL_MS);
    }

    return () => {
      // Remove this component's listener
      globalListeners.delete(listener);

      // Clear global interval only when no more listeners
      if (globalListeners.size === 0 && globalIntervalId) {
        clearInterval(globalIntervalId);
        globalIntervalId = null;
      }
    };
  }, [fetchNotificationCount, isAuthenticated, isExecutive]);

  // Responsive breakpoints - must match MainNavigator's drawer breakpoint (768px)
  // MainNavigator uses drawerType 'front' (overlay) when width < 768
  // so we need the menu button to show at the same breakpoint
  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 768;
  const isMediumScreen = isTablet;

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchQuery);
    } else if (searchQuery.trim()) {
      // Default: navigate to search results screen
      navigation.navigate('SearchStack' as never, {
        screen: 'SearchResults',
        params: { query: searchQuery.trim() }
      } as never);
    }
  };

  const handleNotificationPress = () => {
    if (onNotificationPress) {
      onNotificationPress();
    } else {
      // Default: navigate to notifications
      navigation.navigate('NotificationsStack' as never);
    }
  };

  const handleThemeToggle = () => {
    console.log('Theme toggle pressed, current isDark:', isDark);
    if (onThemeToggle) {
      onThemeToggle();
    } else {
      // Toggle theme using context
      toggleTheme();
      console.log('Theme toggled to:', !isDark);
    }
  };

  const handleSettingsPress = () => {
    console.log('Settings button pressed');
    try {
      // Navigate to Settings screen inside SettingsStack
      navigation.navigate('SettingsStack', { screen: 'Settings' } as never);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleMenuPress = () => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  };

  const handleGoBack = () => {
    if (canGoBack) {
      navigation.goBack();
    }
  };

  // Dynamic theme styles
  const themedStyles = {
    header: {
      backgroundColor: colors.card,
      borderBottomColor: colors.cardBorder,
    },
    pressedBtn: {
      backgroundColor: colors.gray100,
    },
    searchBtn: {
      backgroundColor: colors.gray100,
    },
    searchBtnText: {
      color: colors.gray700,
    },
    searchInputWrapper: {
      backgroundColor: colors.gray50,
      borderColor: colors.gray200,
    },
    searchInput: {
      color: colors.gray700,
    },
    headerTitle: {
      color: colors.gray900,
    },
  };

  return (
    <View style={[styles.header, themedStyles.header, isSmallScreen && styles.headerSmall]}>
      {/* Left: Icons */}
      <View style={styles.headerLeft}>
        {/* Menu button for small screens */}
        {isSmallScreen && Platform.OS === 'web' && (
          <TouchableOpacity style={styles.headerIconBtn} onPress={handleMenuPress}>
            <MenuIcon size={24} color={colors.iconMuted} />
          </TouchableOpacity>
        )}
        {/* Settings button */}
        <Pressable
          style={({ pressed }) => [
            styles.headerIconBtn,
            pressed && themedStyles.pressedBtn,
            Platform.OS === 'web' && { cursor: 'pointer' } as any,
          ]}
          onPress={handleSettingsPress}
          accessibilityRole="button"
          accessibilityLabel={t('navigation.settings')}
        >
          <SettingsIcon size={22} color={colors.iconMuted} />
        </Pressable>
        {/* Dark/Light mode toggle */}
        <Pressable
          style={({ pressed }) => [
            styles.headerIconBtn,
            pressed && themedStyles.pressedBtn,
            Platform.OS === 'web' && { cursor: 'pointer' } as any,
          ]}
          onPress={handleThemeToggle}
          accessibilityRole="button"
          accessibilityLabel={isDark ? t('settings.lightMode') : t('settings.darkMode')}
        >
          {isDark ? <SunIcon size={22} color={colors.iconMuted} /> : <MoonIcon size={22} color={colors.iconMuted} />}
        </Pressable>
        {/* Notifications bell with real count - hidden for executive users */}
        {!isExecutive && (
          <TouchableOpacity style={styles.headerIconBtn} onPress={handleNotificationPress}>
            <View style={styles.notificationBadge}>
              <BellIcon size={22} color={colors.iconMuted} />
              {notificationCount > 0 && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{notificationCount > 99 ? '99+' : notificationCount}</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        )}
      </View>

      {/* Center: Search - hide on small screens */}
      {showSearch && !isSmallScreen && (
        <View style={styles.searchContainer}>
          <TouchableOpacity style={[styles.searchBtn, themedStyles.searchBtn]} onPress={handleSearch}>
            <Text style={[styles.searchBtnText, themedStyles.searchBtnText]}>{t('common.search')}</Text>
          </TouchableOpacity>
          <View style={[styles.searchInputWrapper, themedStyles.searchInputWrapper, isMediumScreen && styles.searchInputWrapperMedium]}>
            <TextInput
              style={[styles.searchInput, themedStyles.searchInput]}
              placeholder={t('dashboard.searchPlaceholder')}
              placeholderTextColor={colors.textLight}
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSubmitEditing={handleSearch}
            />
            <SearchIcon size={20} color={colors.textLight} />
          </View>
        </View>
      )}

      {/* Right: Title + Back Button */}
      <View style={styles.headerRight}>
        <Text style={[styles.headerTitle, themedStyles.headerTitle, isSmallScreen && styles.headerTitleSmall]} numberOfLines={1}>
          {isSmallScreen ? t('auth.systemName') : displayTitle}
        </Text>
        {/* Back Button - shows when there's navigation history */}
        {canGoBack && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleGoBack}
            accessibilityLabel={t('common.back')}
          >
            <BackArrowIcon size={24} color={colors.iconMuted} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["4"],
    backgroundColor: COLORS.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: COLORS.gray200,
  },
  headerSmall: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  headerIconBtn: {
    padding: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    minWidth: 40,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pressedBtn: {
    opacity: 0.7,
    backgroundColor: COLORS.gray100,
  },
  notificationBadge: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -4,
    end: -4,
    backgroundColor: COLORS.danger,
    borderRadius: tokens.radii.absolute["radius-10"],
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: COLORS.white,
    fontSize: 10,
    fontFamily: FONTS.medium,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  searchBtn: {
    backgroundColor: COLORS.gray100,
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  searchBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.gray700,
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.gray50,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: COLORS.gray200,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    minWidth: 280,
  },
  searchInputWrapperMedium: {
    minWidth: 200,
  },
  searchInput: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.gray700,
    textAlign: getTextAlign(isRTL),
    paddingEnd: tokens.spacing.absolute["2"],
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  headerTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: COLORS.gray900,
  },
  headerTitleSmall: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    maxWidth: 120,
  },
  backButton: {
    padding: tokens.spacing.absolute["2"],
  },
});

const styles = getStyles(true);
